import React, { forwardRef } from 'react';
import styles from './Input.module.css';

type InputSize = 'sm' | 'md' | 'lg';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: InputSize;
  variant?: 'default' | 'filled' | 'outlined';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
  id?: string;
  required?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  size = 'md',
  variant = 'default',
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  id,
  required = false,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const stylesRecord = styles as Record<string, string>;
  const inputClasses = [
    stylesRecord['input'],
    stylesRecord[`input--${size}`],
    stylesRecord[`input--${variant}`],
    error && stylesRecord['input--error'],
    leftIcon && stylesRecord['input--hasLeftIcon'],
    rightIcon && stylesRecord['input--hasRightIcon'],
    fullWidth && stylesRecord['input--fullWidth'],
    className
  ].filter(Boolean).join(' ');

  const containerClasses = [
    stylesRecord['inputContainer'],
    fullWidth && stylesRecord['inputContainer--fullWidth']
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={inputId} className={stylesRecord['label'] || ''}>
          {label}
          {required && <span className={stylesRecord['required'] || ''}>*</span>}
        </label>
      )}

      <div className={stylesRecord['inputWrapper'] || ''}>
        {leftIcon && (
          <div className={stylesRecord['leftIcon'] || ''}>
            {leftIcon}
          </div>
        )}

        <input 
          aria-label="text input"
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...props}
        />

        {rightIcon && (
          <div className={stylesRecord['rightIcon'] || ''}>
            {rightIcon}
          </div>
        )}
      </div>

      {error && (
        <div className={stylesRecord['errorMessage'] || ''}>
          {error}
        </div>
      )}

      {!error && helperText && (
        <div className={stylesRecord['helperText'] || ''}>
          {helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;

